/**
 * RM-359 — RM's review settings on a restaurant, as Edit Restaurant shows and
 * saves them: one switch per member category (Star, Verified, Regular).
 * The same rule as the server's lib/reservationReview (Kapil and the product
 * agent, 10/27; Verified off, Kapil 10/29): a non-Preferred restaurant
 * reviews Regular members' requests, not Verified members' or Stars'; a
 * Preferred one reviews none. Saved settings
 * hold while the restaurant keeps the Preferred status they were set under
 * (forPreferred), so one that changes status is back on its defaults.
 */

export const REVIEW_CATEGORIES = [
  { key: 'star', label: 'Star members' },
  { key: 'verified', label: 'Verified members' },
  { key: 'regular', label: 'Regular members' },
]

export const REVIEW_DEFAULTS = {
  preferred: { star: false, verified: false, regular: false },
  notPreferred: { star: false, verified: false, regular: true },
}

const isPreferred = restaurant => restaurant?.status === 'Preferred'

/** The saved settings when they apply to the restaurant's status now, else null. */
export function settingsInForce(restaurant) {
  const set = restaurant?.reviewSettings

  return set && typeof set === 'object' && set.forPreferred === isPreferred(restaurant) ? set : null
}

/** Is this category reviewed at the restaurant now (its setting, or the default)? */
export function reviewOn(restaurant, key) {
  const set = settingsInForce(restaurant)
  if (set && typeof set[key] === 'boolean') return set[key]

  return (isPreferred(restaurant) ? REVIEW_DEFAULTS.preferred : REVIEW_DEFAULTS.notPreferred)[key]
}

/** The settings to save after a switch changes: all three as shown, under the status shown. */
export function withReview(restaurant, key, value) {
  const current = Object.fromEntries(REVIEW_CATEGORIES.map(c => [c.key, reviewOn(restaurant, c.key)]))

  return { ...current, [key]: !!value, forPreferred: isPreferred(restaurant) }
}

/** The line under the switches: what the default is for this restaurant's status. */
export function defaultsHint(restaurant) {
  return isPreferred(restaurant)
    ? 'Default for a Preferred restaurant: no review'
    : 'Default for a non-Preferred restaurant: Regular reviewed, Verified and Star not'
}
